import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api_editPartyCatering } from '../../../api/catering/partyCatering'; // Adjusted API import for editing and fetching event types and sub types
import { toast } from 'react-hot-toast';
import { myToastWarning } from '../../../helpers/myToast';

type SubType = {
	_id: string;
	name: string;
	description: string;
	price: {
		from: number;
		to: number;
	};
};

type PartyCatering = {
	_id: string;
	name: string;
	description: string;
	price: {
		from: number;
		to: number;
	};
	type: string;
	subPartyCatering?: SubType[];
};

const EditPartyCatering = () => {
	const partyCatering: PartyCatering = useLocation().state;
	const [loading, setLoading] = useState<boolean>(false);
	const [name, setName] = useState<string>(partyCatering?.name || '');
	const [description, setDescription] = useState<string>(partyCatering?.description || '');
	const [fromPrice, setFromPrice] = useState<number | string>(partyCatering?.price?.from || '');
	const [toPrice, setToPrice] = useState<number | string>(partyCatering?.price?.to || '');
	const [subPartyCatering, setSubPartyCatering] = useState<SubType[]>(partyCatering?.subPartyCatering || []);
	const navigate = useNavigate();

	useEffect(() => {
		// Fetch subPartyCatering only if it exists in partyCatering
		if (partyCatering.subPartyCatering) {
			setSubPartyCatering([...partyCatering.subPartyCatering]); // Copy array to state
		} else {
			setSubPartyCatering([]); // Clear subPartyCatering state if it doesn't exist
		}
	}, [partyCatering.subPartyCatering]);

	const handleSubTypeChange = (index: number, field: keyof SubType | keyof SubType['price'], value: string | number) => {
		const updatedSubPartyCatering = [...subPartyCatering];
		if (field === 'from' || field === 'to') {
			updatedSubPartyCatering[index].price = {
				...updatedSubPartyCatering[index].price,
				[field]: Number(value),
			};
		} else {
			updatedSubPartyCatering[index] = {
				...updatedSubPartyCatering[index],
				[field]: value,
			};
		}
		setSubPartyCatering(updatedSubPartyCatering);
	};

	const editPartyCatering = async () => {
		if (!name) {
			myToastWarning('Please Enter Name');
			return;
		}
		try {
			setLoading(true);
			const payload = {
				name,
				description,
				price: {
					from: Number(fromPrice),
					to: Number(toPrice),
				},
				subPartyCatering: subPartyCatering.map((subType) => ({
					_id: subType._id,
					name: subType.name,
					description: subType.description,
					price: {
						from: subType.price.from,
						to: subType.price.to,
					},
				})),
			};

			const res = await api_editPartyCatering(payload, partyCatering._id);
			const message = typeof res.message === 'string' ? res.message : 'Success';
			if (res.isSuccess) {
				toast.success(message || 'Event type updated successfully');
				setTimeout(() => {
					navigate(-1);
				}, 100);
			} else {
				toast.error(message || 'Failed to update event type');
			}
		} catch (error) {
			console.error('Error editing event type:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, padding: '40px' }}>
			<div className='mb-3'>
				<h3>Edit Event Type</h3>
			</div>

			<div className='mb-3'>
				<label htmlFor='name' className='form-label'>
					Name
				</label>
				<input
					id='name'
					value={name}
					onChange={(e) => setName(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter name'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='description' className='form-label'>
					Description
				</label>
				<input
					id='description'
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter description'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='fromPrice' className='form-label'>
					From Price
				</label>
				<input
					id='fromPrice'
					type='number'
					value={fromPrice}
					onChange={(e) => setFromPrice(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter from price'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='toPrice' className='form-label'>
					To Price
				</label>
				<input
					id='toPrice'
					type='number'
					value={toPrice}
					onChange={(e) => setToPrice(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter to price'
				/>
			</div>

			{subPartyCatering.length > 0 && (
				<div className='mb-3'>
					<label className='form-label'>Edit Sub Party Catering:</label>
					{subPartyCatering.map((subType, index) => (
						<div key={subType._id} className='mb-3'>
							<label htmlFor={`subTypeName-${index}`} className='form-label'>
								Name
							</label>
							<input
								id={`subTypeName-${index}`}
								value={subType.name}
								onChange={(e) => handleSubTypeChange(index, 'name', e.target.value)}
								style={{ width: '400px' }}
								className='form-control'
								placeholder='Enter name'
							/>

							<label htmlFor={`subTypeDesc-${index}`} className='form-label'>
								Description
							</label>
							<input
								id={`subTypeDesc-${index}`}
								value={subType.description}
								onChange={(e) => handleSubTypeChange(index, 'description', e.target.value)}
								style={{ width: '400px' }}
								className='form-control'
								placeholder='Enter description'
							/>

							<label htmlFor={`subTypeFromPrice-${index}`} className='form-label'>
								From Price
							</label>
							<input
								id={`subTypeFromPrice-${index}`}
								type='number'
								value={subType.price.from}
								onChange={(e) => handleSubTypeChange(index, 'from', e.target.value)}
								style={{ width: '400px' }}
								className='form-control'
								placeholder='Enter from price'
							/>

							<label htmlFor={`subTypeToPrice-${index}`} className='form-label'>
								To Price
							</label>
							<input
								id={`subTypeToPrice-${index}`}
								type='number'
								value={subType.price.to}
								onChange={(e) => handleSubTypeChange(index, 'to', e.target.value)}
								style={{ width: '400px' }}
								className='form-control'
								placeholder='Enter to price'
							/>
						</div>
					))}
				</div>
			)}

			<button onClick={editPartyCatering} type='button' className='btn btn-lg btn-primary' disabled={loading}>
				{loading ? 'Loading..' : 'Submit'}
			</button>
		</div>
	);
};

export default EditPartyCatering;
